// ============================================================================
// GTO CONTEXT - what spot is the player in right now?
// ----------------------------------------------------------------------------
// Training screens publish the player's seat and stack here; the floating
// Charts button reads it so the drawer opens on the matching chart. Tiny
// store, no dependencies: set / subscribe / hook.
// ============================================================================
import { useEffect, useState } from 'react';
import type { GtoSituation } from '../../components/training/GtoChart';

export interface GtoContext {
  position?: string;
  stackBB?: number | null;
  situation?: GtoSituation;
  /** The hand the player is holding right now, as a chart code ("AKs"). Training only. */
  hand?: string | null;
  /** For situation 'vs': which facing-a-raise chart to open (a VS_OPEN_SPOTS key). */
  spot?: string;
  /** Where the hand is. Postflop the preflop charts no longer apply and the drawer says so. */
  stage?: 'preflop' | 'postflop';
  /** One line about the action, e.g. "CO raised in front of you". */
  actionNote?: string;
  /** Short label shown on the button, e.g. "BTN · 42 BB" */
  label?: string;
}

let current: GtoContext = {};
const listeners = new Set<(c: GtoContext) => void>();

const KEYS: Array<keyof GtoContext> = ['position', 'stackBB', 'situation', 'hand', 'spot', 'stage', 'actionNote', 'label'];
const same = (a: GtoContext, b: GtoContext) => KEYS.every((k) => (a[k] ?? null) === (b[k] ?? null));

export function setGtoContext(next: GtoContext): void {
  // Publishers call this on every game-state tick; only wake the button
  // (and an open drawer) when something on the chart actually changed.
  if (same(current, next)) return;
  current = next;
  listeners.forEach((l) => l(current));
}
export function clearGtoContext(): void { setGtoContext({}); }
export function getGtoContext(): GtoContext { return current; }

export function useGtoContext(): GtoContext {
  const [ctx, setCtx] = useState<GtoContext>(current);
  useEffect(() => {
    listeners.add(setCtx);
    setCtx(current);
    return () => { listeners.delete(setCtx); };
  }, []);
  return ctx;
}

/**
 * Which "someone raised" chart fits: me in the big blind against the button,
 * or in position against an early or late raise. Out of position against a
 * non-button raise is approximated by the same two charts.
 */
export function vsSpotFor(myPosition: string, raiserPosition: string): string {
  if (myPosition === 'BB' && (raiserPosition === 'BTN' || raiserPosition === 'SB')) return 'Big blind vs button raise';
  const late = raiserPosition === 'CO' || raiserPosition === 'BTN' || raiserPosition === 'SB';
  return late ? 'In position vs late raise' : 'In position vs early raise';
}

/**
 * Seat name from table geometry: players in seat order, the dealer index,
 * and my index. Works for 2 to 10 players.
 */
export function positionFromSeat(playerCount: number, dealerIndex: number, myIndex: number): string {
  if (playerCount <= 0 || myIndex < 0) return 'BTN';
  if (playerCount === 2) return myIndex === dealerIndex ? 'BTN' : 'BB';
  const dist = ((myIndex - dealerIndex) % playerCount + playerCount) % playerCount;
  if (dist === 0) return 'BTN';
  if (dist === 1) return 'SB';
  if (dist === 2) return 'BB';
  // Seats after the big blind, in order, up to the cutoff
  const after = playerCount - 3; // seats between BB and BTN
  const idx = dist - 3;          // 0 = first to act after BB
  const fromEnd = after - 1 - idx; // 0 = cutoff
  if (fromEnd === 0) return 'CO';
  if (fromEnd === 1) return 'HJ';
  if (fromEnd === 2) return 'LJ';
  if (idx === 0) return 'UTG';
  if (idx === 1) return 'UTG+1';
  return 'MP';
}
